import Hammer from 'hammerjs';
import Observer from '../../common/scripts/observer';

const observer = new Observer();

class TouchZoom {
    constructor(scalingItem) {
        this.activeClass = 'is-active';
        this.scalingItem = scalingItem;
        this.outerOptions = this._getOuterOptions();
        this.setOptions(this.outerOptions);
        this.initHammer();
    }

    _getOuterOptions() {
        const minScale = this.scalingItem.dataset.minscale || 1;
        const maxScale = this.scalingItem.dataset.maxscale || 3;

        return {
            minScale,
            maxScale
        };
    }

    /**
     * Установка параметров (мин. и макс. масштабирование) и прочих переменных
     * @param {object} outerOptions - внешние параметры
     */
    setOptions(outerOptions) {
        // стартовая позиция translate и scale
        this.startX = 0;
        this.startY = 0;
        this.startScale = 1;

        // стартовая позиция transform
        this.transform = {
            translate: {
                x: 0,
                y: 0
            },
            scale: 1
        };

        this.options = {
            minScale: 1,
            maxScale: 3
        };

        if (typeof outerOptions !== 'object') {
            return;
        }

        this.options = Object.assign(this.options, outerOptions);
    }

    /**
     * Инициализация хаммера
     */
    initHammer() {
        this.ham = new Hammer(this.scalingItem, {
            domEvents: true
        });

        // для масштабирования
        this.ham.get('pinch').set({enable: true});
        // для движения в разные направления
        this.ham.get('pan').set({direction: Hammer.DIRECTION_ALL});

        this.bindTouchEvents();
    }

    /**
     * Привязываем тач-события
     */
    bindTouchEvents() {
        this.ham.on('pinchstart pinchmove pinchend', this.onPinch.bind(this));
        this.ham.on('pinchend', this.onPinchEnd.bind(this));
        this.ham.on('panstart panmove', this.onPan.bind(this));
        this.ham.on('panend', this.onPanEnd.bind(this));
    }

    /**
     * Событие при движении в разные направления
     * @param {event} event - объект события
     */
    onPan(event) {
        const currentScale = this.transform.scale;

        // запрет на движение, когда картинка достигла минимального значения скейла
        if (currentScale <= this.options.minScale) {
            return;
        }

        // расчет стартового текущего положения
        if (event.type === 'panstart') {
            this.startX = this.transform.translate.x ? this.transform.translate.x : 0;
            this.startY = this.transform.translate.y ? this.transform.translate.y : 0;
        }

        const deltaCalcX = this.startX + event.deltaX;
        const deltaCalcY = this.startY + event.deltaY;

        const deltaCalc = this.getUpdatedTranslate([deltaCalcX, deltaCalcY]);

        this.transform.translate = {
            x: deltaCalc[0],
            y: deltaCalc[1]
        };

        this.updateElementTransform();
    }

    /**
     * Окончание движения в разные направления при котором картинка возвращается в изначальное положение, если scale становится минимальным
     */
    onPanEnd() {
        if (this.transform.scale > this.options.minScale) {
            return;
        }
        this.resetTransform();
        this.updateElementTransform();
    }

    /**
     * Событие при мультитач движении для масштабирования
     * @param {event} event - объект события
     */
    onPinch(event) {
        if (event.type === 'pinchend') {
            if (this.transform.scale === this.options.minScale) {
                this.scalingItem.classList.remove(this.activeClass);
            }
            observer.publish('isPinchEnd', this.transform.scale);
        }
        // расчет стартового текущего положения
        if (event.type === 'pinchstart') {
            this.scalingItem.classList.add(this.activeClass);
            this.startScale = this.transform.scale ? this.transform.scale : 1;
        }

        this.setAbsTranslateValue();
        this.transform.scale = this.getUpdatedScale(this.startScale * event.scale);
        this.updateElementTransform();
    }

    /**
     * Окончание мультитач движения для масштабирования (происходит изменение translate для того, чтобы значение translate было в рамках абс. значений)
     */
    onPinchEnd() {
        const deltaCalcX = this.transform.translate.x;
        const deltaCalcY = this.transform.translate.y;
        const deltaCalc = this.getUpdatedTranslate([deltaCalcX, deltaCalcY]);

        this.transform.translate = {
            x: deltaCalc[0],
            y: deltaCalc[1]
        };
        this.updateElementTransform();
    }

    /**
     * Обновление css-свойства transform у элемента, который нужно масштабировать и двигать
     */
    updateElementTransform() {
        this.scalingItem.style.transform = this.transform.scale === this.options.minScale ?
            `` :
            `translate3d(${this.transform.translate.x}px,
            ${this.transform.translate.y}px, 0) scale(${this.transform.scale},
            ${this.transform.scale}) rotate(0deg)`;
    }

    /**
     * Получение обновленных transform: translateX, translateY после проверки с фбс.значением
     * @param {array} deltaCalc - предварительные расчётные данные transform: translateX, translateY в виде массива
     * @return {array} - обновленные данные в виде массива
     */
    getUpdatedTranslate(deltaCalc) {
        let [deltaCalcX, deltaCalcY] = deltaCalc;

        if (this.absTranslateX <= deltaCalcX) {
            deltaCalcX = this.absTranslateX;
        } else if (-this.absTranslateX >= deltaCalcX) {
            deltaCalcX = -this.absTranslateX;
        }

        if (this.absTranslateY <= deltaCalcY) {
            deltaCalcY = this.absTranslateY;
        } else if (-this.absTranslateY >= deltaCalcY) {
            deltaCalcY = -this.absTranslateY;
        }

        return [deltaCalcX, deltaCalcY];
    }

    /**
     * Установка абс. значений translate, которые ограничивают смещение
     */
    setAbsTranslateValue() {
        const scalingWidth = this.scalingItem.offsetWidth;
        const scalingHeight = this.scalingItem.offsetHeight;
        const currentScalingItemWidth = scalingWidth * this.transform.scale;
        const currentScalingItemHeight = scalingHeight * this.transform.scale;

        this.absTranslateX = (currentScalingItemWidth - scalingWidth) / 2;
        this.absTranslateY = (currentScalingItemHeight - scalingHeight) / 2;
    }

    /**
     * Получение обновленных данных для св-ва transform: scale, с учетом проверки на мин. и макс
     * @param {number} calcScale - предварительный расчет для св-ва transform: scale
     * @return {number} - обновленный расчет после проверки
     */
    getUpdatedScale(calcScale) {
        let extreme = calcScale;

        if (calcScale < this.options.minScale) {
            extreme = this.options.minScale;
        } else if (calcScale > this.options.maxScale) {
            extreme = this.options.maxScale;
        }

        return extreme;
    }

    /**
     * Сброс св-ва transform до начального состояния
     */
    resetTransform() {
        this.transform = {
            translate: {
                x: 0,
                y: 0
            },
            scale: 1
        };
    }
}

export default TouchZoom;
